import { Edit } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { Product } from '../../apiTypes';
import DataTable, { Column } from '../../components/DataTable';
import Image from '../../components/Image';
import PrimaryButton from '../../components/PrimaryButton';
import { t } from '../../constants/translations';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import EditProduct from './EditProduct';

type Row = DbDocument<Product>;

const ProductList = () => {
  const { lang } = useLocale();
  const { products } = useCafe();
  const [open, setOpen] = useState(false);
  const [product, setProduct] = useState<DbDocument<Product> | null>(null);

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'imageURL',
        title: '',
        render: (r: Row) => <Image src={r.imageUrl} />,
        sx: { width: 40, pr: 0 },
      },
      {
        name: 'name',
        title: t.name[lang],
        align: 'left',
        render: (r: Row) => <Typography>{r.name}</Typography>,
        search: true,
      },
      {
        name: 'category',
        title: t.category[lang],
        render: (r: Row) => <Typography>{r.category}</Typography>,
        filterable: true,
      },
      {
        name: 'unit',
        title: t.unit[lang],
        render: (r: Row) => <Typography>{t[r.unit][lang]}</Typography>,
      },
      {
        name: 'qpp',
        title: t.packaging[lang],
        render: (r: Row) => <Typography>{r.qpp ? `${r.qpp} ${t[r.unit][lang]}` : '-'}</Typography>,
      },
      {
        name: 'provider',
        title: t.provider[lang],
        render: (r: Row) => <Typography>{r.provider}</Typography>,
        filterable: true,
      },
      {
        name: 'ean',
        title: t.article[lang],
        render: (r: Row) => <Typography>{r.ean}</Typography>,
      },
      {
        name: 'edit',
        title: t.edit[lang],
        render: (r: Row) => (
          <IconButton
            onClick={() => {
              setProduct(r);
              setOpen(true);
            }}
          >
            <Edit />
          </IconButton>
        ),
      },
    ],
    [lang]
  );

  return (
    <Box sx={{ maxWidth: 1200, m: '0 auto' }}>
      <DataTable
        columns={columns}
        rows={products}
        searchBy="name"
        dataTestIdPrefix="product"
        actionButton={
          <PrimaryButton onClick={() => setOpen(true)} variant="contained">
            {t.add_product[lang]}
          </PrimaryButton>
        }
      />

      {open && (
        <EditProduct
          product={product}
          open={open}
          onClose={() => {
            setOpen(false);
            setProduct(null);
          }}
        />
      )}
    </Box>
  );
};

export default ProductList;
